import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Modals from "../../utilisateurs/components/ModalAddUser";
import affiliationService from "../../../../service/affiliation.service";
import Swal from "sweetalert2";

const AffiliationFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  affiliationToEdit = null,
}) => {
  const [formData, setFormData] = useState({
    name_aff: "",
    country_aff: "",
    type_aff: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Set form data when editing
  useEffect(() => {
    if (affiliationToEdit) {
      setFormData({
        name_aff: affiliationToEdit.name_aff || "",
        country_aff: affiliationToEdit.country_aff || "",
        type_aff: affiliationToEdit.type_aff || "",
      });
    } else {
      // Reset form when creating
      setFormData({
        name_aff: "",
        country_aff: "",
        type_aff: "",
      });
    }
    setErrors({});
  }, [affiliationToEdit, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error on field change
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      let response;

      // Handle update vs create
      if (affiliationToEdit) {
        response = await affiliationService.updateAffiliation(
          affiliationToEdit.id_aff,
          formData
        );

        // Show success notification
        Swal.fire({
          icon: "success",
          title: "Affiliation mise à jour",
          text: "L'affiliation a été mise à jour avec succès",
          confirmButtonColor: "#57A863",
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        response = await affiliationService.createAffiliations(formData);

        // Show success notification
        Swal.fire({
          icon: "success",
          title: "Affiliation créée",
          text: "La nouvelle affiliation a été ajoutée avec succès",
          confirmButtonColor: "#57A863",
          timer: 3000,
          timerProgressBar: true,
        });
      }

      onSuccess(response.data, affiliationToEdit ? "update" : "create");
      onClose();
    } catch (error) {
      // Handle validation errors
      if (
        error.response?.data?.message &&
        typeof error.response.data.message === "object"
      ) {
        setErrors(error.response.data.message);

        // Display error message
        Swal.fire({
          icon: "error",
          title: "Erreur de validation",
          text: "Veuillez corriger les erreurs dans le formulaire",
          confirmButtonColor: "#d33",
        });
      } else {
        // Display general error
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text:
            error.response?.data?.message ||
            "Une erreur est survenue lors du traitement de votre demande",
          confirmButtonColor: "#d33",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modals
      isOpen={isOpen}
      onClose={onClose}
      width="64"
      title={
        affiliationToEdit ? "Modifier l'affiliation" : "Ajouter une affiliation"
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-1">
          <label
            htmlFor="name_aff"
            className="block text-sm font-medium text-gray-700"
          >
            Nom de l'affiliation <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name_aff"
            id="name_aff"
            value={formData.name_aff}
            onChange={handleChange}
            placeholder="Ex: Université de Paris"
            className={`focus:border-transparent w-full rounded-lg border ${
              errors.name_aff ? "border-red-300 bg-red-50" : "border-gray-300"
            } px-4 py-2.5 shadow-sm transition-all focus:outline-none focus:ring-2 ${
              errors.name_aff ? "focus:ring-red-500" : "focus:ring-[#57A863]"
            }`}
            required
          />
          {errors.name_aff && (
            <p className="mt-1 text-sm text-red-600">{errors.name_aff}</p>
          )}
        </div>

        <div className="space-y-1">
          <label
            htmlFor="country_aff"
            className="block text-sm font-medium text-gray-700"
          >
            Pays de l'affiliation <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="country_aff"
            id="country_aff"
            value={formData.country_aff}
            onChange={handleChange}
            placeholder="Ex: France"
            className={`focus:border-transparent w-full rounded-lg border ${
              errors.country_aff
                ? "border-red-300 bg-red-50"
                : "border-gray-300"
            } px-4 py-2.5 shadow-sm transition-all focus:outline-none focus:ring-2 ${
              errors.country_aff ? "focus:ring-red-500" : "focus:ring-[#57A863]"
            }`}
            required
          />
          {errors.country_aff && (
            <p className="mt-1 text-sm text-red-600">{errors.country_aff}</p>
          )}
        </div>

        <div className="space-y-1">
          <label
            htmlFor="type_aff"
            className="block text-sm font-medium text-gray-700"
          >
            Type d'affiliation <span className="text-red-500">*</span>
          </label>
          <select
            name="type_aff"
            id="type_aff"
            value={formData.type_aff}
            onChange={handleChange}
            className={`focus:border-transparent w-full rounded-lg border ${
              errors.type_aff ? "border-red-300 bg-red-50" : "border-gray-300"
            } px-4 py-2.5 shadow-sm transition-all focus:outline-none focus:ring-2 ${
              errors.type_aff ? "focus:ring-red-500" : "focus:ring-[#57A863]"
            }`}
            required
          >
            <option value="">Sélectionnez un type</option>
            <option value="university">Université</option>
            <option value="laboratory">Laboratoire</option>
          </select>
          {errors.type_aff && (
            <p className="mt-1 text-sm text-red-600">{errors.type_aff}</p>
          )}
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <motion.button
            type="button"
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="rounded-lg bg-gray-200 px-5 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-300"
          >
            Annuler
          </motion.button>

          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex items-center justify-center rounded-lg bg-[#57A863] px-5 py-2.5 font-medium text-white transition-all hover:bg-green-800 hover:shadow-md ${
              isSubmitting ? "cursor-not-allowed opacity-70" : ""
            }`}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="mr-2 h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Traitement...
              </>
            ) : affiliationToEdit ? (
              "Mettre à jour"
            ) : (
              "Ajouter"
            )}
          </motion.button>
        </div>
      </form>
    </Modals>
  );
};

export default AffiliationFormModal;
